/**
 * Компания глазами команды портала (ЛЦ-11, ЛЦ-12): люди и их активность, входы команды портала, «Войти».
 * Владельцу портала эти блоки добавлены в его карточку компании; администратору портала — отдельная карточка без
 * счетов, ключей, заметок к лицензии и действий с лицензией.
 */
import {
  type ConsoleCompanyDetail,
  ENTRY_END_TITLES,
  ENTRY_MODE_TITLES,
  LICENSE_KIND_TITLES,
  MONEY_LEVELS,
  type PlatformEntryLogRow,
} from '@epc/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { LogIn } from 'lucide-react';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { dateTime, relative } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { KeyValue, Panel } from '../../ui/layout';
import { daysLeftText, LicenseEvents, UsageBars } from '../admin/LicensePage';
import { EntryDialog, peopleText, roleTitle, usePlatformCan } from './console-shared';
import { StateBadge } from './shared';

export const companyConsoleQuery = (id: string) =>
  queryOptions({
    queryKey: ['platform', 'companies', id],
    queryFn: () => api<ConsoleCompanyDetail>(`/platform/companies/${id}`),
  });

const KIND_TITLES: Record<string, string> = { staff: 'сотрудник', external: 'внешний участник' };

function moneyTitle(level: string): string {
  return (MONEY_LEVELS as Record<string, string>)[level] ?? level;
}

/** «Войти в компанию» — для своей компании кнопки нет: в неё переключаются в меню профиля */
export function EnterCompanyButton({ tenant }: { tenant: { id: string; name: string } }) {
  const me = useMe();
  const can = usePlatformCan('entry');
  const [open, setOpen] = useState(false);
  if (!can || me.tenants.some((t) => t.id === tenant.id)) return null;
  return (
    <>
      <Button variant="primary" size="sm" icon={<LogIn className="size-4" />} onClick={() => setOpen(true)}>
        Войти в компанию
      </Button>
      {open ? <EntryDialog tenant={tenant} onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/** Люди компании: роль, уровень денег, последняя активность, последний вход с адресом и устройством */
export function CompanyPeoplePanel({ data }: { data: ConsoleCompanyDetail }) {
  const people = data.people;
  const active = people.filter((p) => p.status === 'active');
  return (
    <Panel
      title="Люди и активность"
      meta={`${peopleText(data.company.staff, data.company.external)} · работали за 7 дней: ${data.company.active7}`}
      bodyClassName="p-0"
    >
      {!people.length ? (
        <div className="p-5">
          <EmptyState title="Людей пока нет">Администратор компании ещё не принял приглашение.</EmptyState>
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {[...active, ...people.filter((p) => p.status !== 'active')].map((p) => (
            <li key={p.userId}>
              <Link
                to="/platform/users/$id"
                params={{ id: p.userId }}
                className="flex min-h-11 flex-col gap-0.5 px-5 py-2.5 hover:bg-surface-2"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-ink [overflow-wrap:anywhere]">{p.fullName}</span>
                  {p.isOwner ? <Badge tone="ink">владелец</Badge> : null}
                  {p.kind !== 'staff' ? <Badge>{KIND_TITLES[p.kind] ?? p.kind}</Badge> : null}
                  {p.status !== 'active' ? <Badge tone="neutral">отключён в компании</Badge> : null}
                  {!p.isActive ? <Badge tone="danger">вход заблокирован</Badge> : null}
                </span>
                <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                  {p.email}
                  {p.position ? ` · ${p.position}` : ''}
                  {p.roles.length ? ` · ${p.roles.join(', ')}` : ''} · деньги: {moneyTitle(p.moneyLevel)}
                </span>
                <span className="text-[13px] text-ink-2">
                  В портале {p.lastSeenAt ? relative(p.lastSeenAt) : 'давно или не работал в этой компании'}
                  {p.lastIp ? ` · последний вход с ${p.lastIp}` : ''}
                  {p.device ? `, ${p.device}` : ''}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

export function EntriesList({
  entries,
  showCompany,
}: {
  entries: PlatformEntryLogRow[];
  showCompany?: boolean;
}) {
  if (!entries.length)
    return <p className="px-5 py-4 text-[14px] text-ink-2">Команда портала в компанию не входила.</p>;
  return (
    <ul className="divide-y divide-line">
      {entries.map((e) => {
        const open = !e.endedAt && Date.parse(e.expiresAt) > Date.now();
        return (
          <li key={e.id} className="flex flex-col gap-0.5 px-5 py-2.5 text-[14px]">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-ink">
                {showCompany ? (e.tenantName ?? 'компания') : (e.userName ?? 'человек')}
              </span>
              <Badge tone={e.mode === 'edit' ? 'warning' : 'neutral'}>{ENTRY_MODE_TITLES[e.mode]}</Badge>
              {e.money === 'all' ? <Badge tone="ink">с суммами</Badge> : null}
              {open ? <Badge tone="accent">сейчас внутри</Badge> : null}
            </span>
            <span className="text-[13px] text-ink-2">
              {showCompany ? '' : `${roleTitle(e.role)} · `}
              {dateTime(e.startedAt)} —{' '}
              {e.endedAt
                ? `${dateTime(e.endedAt)}${e.endReason ? `, ${ENTRY_END_TITLES[e.endReason]}` : ''}`
                : open
                  ? `до ${dateTime(e.expiresAt)}`
                  : 'время вышло'}
              {e.ip ? ` · ${e.ip}` : ''}
              {e.device ? `, ${e.device}` : ''}
            </span>
            {e.reason ? (
              <span className="text-[13px] text-ink [overflow-wrap:anywhere]">Причина: {e.reason}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export function CompanyEntriesPanel({ entries }: { entries: PlatformEntryLogRow[] }) {
  return (
    <Panel title="Входы команды портала" meta="ЛЦ-11" bodyClassName="p-0">
      <EntriesList entries={entries} />
    </Panel>
  );
}

/** Блоки команды портала в карточке компании владельца портала */
export function CompanyConsoleBlocks({ id }: { id: string }) {
  const q = useQuery(companyConsoleQuery(id));
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  return (
    <>
      <CompanyPeoplePanel data={q.data} />
      <CompanyEntriesPanel entries={q.data.entries} />
    </>
  );
}

/** Карточка компании для администратора портала: лицензия без заметок и действий, люди, входы, журнал */
export function CompanyConsoleCard() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery(companyConsoleQuery(id));
  useCrumbs(q.data ? [{ label: q.data.company.name }] : null, q.error);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data;
  const c = d.company;
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Link
            to="/platform"
            className="inline-flex h-11 items-center text-[14px] text-ink-2 underline-offset-4 hover:underline md:h-8"
          >
            ← Все компании
          </Link>
          <h2 className="text-[20px] leading-7 font-semibold text-ink [overflow-wrap:anywhere]">{c.name}</h2>
          {c.isDemo ? <Badge tone="info">демо</Badge> : null}
          <StateBadge state={d.license.state} />
        </div>
        <EnterCompanyButton tenant={{ id: c.id, name: c.name }} />
      </div>
      {d.license.suspendedReason ? (
        <div className="rounded-md border border-danger/40 bg-danger-soft px-4 py-3 text-[14px] text-ink">
          Доступ приостановлен: {d.license.suspendedReason}. Сотрудники компании не могут войти в портал.
        </div>
      ) : null}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex flex-col gap-5">
          <CompanyPeoplePanel data={d} />
          <CompanyEntriesPanel entries={d.entries} />
        </div>
        <div className="flex flex-col gap-5">
          <Panel title="Лицензия">
            <KeyValue
              items={[
                { label: 'Тариф', value: d.license.planName ?? '—' },
                { label: 'Вид', value: d.license.kind ? LICENSE_KIND_TITLES[d.license.kind] : '—' },
                { label: 'Окончание', value: daysLeftText(d.license.daysLeft, d.license.endsAt) },
                { label: 'Мест', value: d.license.seats ?? 'по тарифу' },
                { label: 'Код компании', value: c.slug },
                { label: 'Входов за 7 дней', value: `${c.logins7}, неудачных ${c.failed7}` },
              ]}
            />
          </Panel>
          <Panel title="Использование лимитов">
            <UsageBars usage={d.usage} />
          </Panel>
          <Panel title="Журнал по компании">
            <LicenseEvents events={d.events} />
          </Panel>
        </div>
      </div>
    </div>
  );
}
